<template>
  <header
    class="header-wrapper"
    :class="{
      'header-with-cover': !!cover,
    }"
    :style="{ backgroundImage: `url(${headerBg})` }"
  >
    <div class="header-content">
      <Breadcrumb class="bread-crumbs" :title="title" />
      <div class="header-info-wrap">
        <img class="header-cover" :src="cover" v-if="cover" alt="title" />
        <div class="header-text">
          <div class="header-info">
            <h1>{{ title }}</h1>
            <p>{{ desc }}</p>
            <div class="meta">
              <Tag v-for="(tag, idx) in tags" :key="idx" :label="tag.name" />
            </div>
          </div>
          <div class="header-bottom">
            <div class="source">
              <img class="source-icon" v-if="sourceIcon" :src="sourceIcon" :alt="source" />
              <span>
                {{ source }}
              </span>
            </div>
            <div class="header-extra" v-if="publishDate && readDuration">
              <div class="flex-center">
                <IconTimer class="text-[16px] mr-2" />
                <span>{{ $t('fa-bu-shi-jian') }} {{ $dayjs(publishDate).format(timeFormat) }}</span>
              </div>
              <div class="divider" />
              <div class="flex-center">
                <IconRead class="text-[16px] mr-2" />
                <span
                  >{{ $t('yu-ji-yue-du-shi-chang') }}{{ readDuration }}
                  {{ $t('fen-zhong') }}
                  {{ !isChinese() ? 'read' : '' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import IconRead from '~/assets/svg/icon-read.svg?component';
import IconTimer from '~/assets/svg/icon-time.svg?component';
import Breadcrumb from './breadcrumb.vue';
import Tag from './tag.vue';
// TODO OSS
import headerBg from '~/assets/png/bg-blog-header.png';
const { $dayjs } = useNuxtApp();
const { t: $t } = useI18n();
const props = withDefaults(
  defineProps<{
    route?: string;
    title?: string;
    desc?: string;
    source?: string;
    cover?: string;
    tags?: { id: number; name: string }[];
    publishDate?: string;
    readDuration?: string;
    sourceIcon?: string;
  }>(),
  {},
);

const timeFormat = computed(() => {
  return isChinese() ? $t('yyyy-nian-mm-yue-dd-ri') : 'MMMM D, YYYY';
});
</script>

<style lang="scss" scoped>
.header-wrapper {
  @apply flex-col items-center;
  width: 100%;
  min-height: 382px;
  margin-bottom: 48px;
  background-repeat: no-repeat;
  background-position: top center;
  background-size: 1920px 720px;
  @media (min-width: 1920px) {
    background-size: cover;
  }

  &.header-with-cover {
    min-height: 470px;
    .header-info-wrap {
      min-height: 406px;
      h1 {
        font-size: 32px;
      }
      p {
        font-size: 16px;
        line-height: 28px;
      }
    }
    .header-cover {
      aspect-ratio: 16/9;
      object-fit: cover;
      max-height: 296px;
      border-radius: 12px;
    }
  }

  .header-content {
    @apply relative;
    width: 1200px;
    height: 100%;
  }
  .bread-crumbs {
    position: absolute;
    top: 24px;
    left: 0;
  }
  .header-info {
    @apply flex-col justify-center items-start;
    h1 {
      color: var(--x-text-1);
      font-size: 40px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      margin: 0;
    }
    p {
      margin: 0;
      margin-top: 12px;
      color: var(--x-text-5);
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
    .meta {
      @apply flex gap-4 mt-10;
      span {
        padding: 4px 12px;
        border-radius: 8px;
        background-color: var(--x-back-1);
        color: var(--x-text-1);
        text-align: center;
        font-size: 16px;
        line-height: normal;
        font-weight: 400;
      }
    }
  }
  .header-bottom {
    @apply flex justify-between items-center w-full py-[13px];
    border-top: 1px solid var(--x-purple-3);
  }
  .source {
    @apply flex-center gap-2;
    color: var(--x-text-5);
    font-size: 14px;
    font-weight: 400;
    line-height: normal;
    .source-icon {
      width: 22px;
      height: 22px;
      border-radius: 50%;
    }
  }
}
.header-extra {
  @apply inline-flex items-center justify-center gap-4 h-[20px];
  color: var(--x-text-5);
  .divider {
    height: 16px;
    width: 1px;
    background-color: var(--x-back-7);
  }
}
.header-info-wrap {
  @apply w-full h-full flex items-start justify-between gap-8 pt-[110px];
  &.header-with-cover {
    height: 296px;
  }
}
.header-text {
  @apply h-full flex flex-col justify-between;
  flex: 1;
}
html[lang='en-US'] {
  .header-wrapper {
    min-height: 472px;
  }
}
</style>
